import { useEffect, useState } from "react";
import { Show } from "@clerk/react";
import { LandingPage } from "./components/landing/LandingPage";
import { Header } from "./components/layout/Header";
import { SettingsDialog } from "./components/settings/SettingsDialog";
import { DevToolsDialog } from "./components/dev/DevToolsDialog";
import { NavPane } from "./components/layout/NavPane";
import { TabBar } from "./components/layout/TabBar";
import { DashboardTab } from "./components/dashboard/DashboardTab";
import { MailView } from "./components/mail/MailView";
import { CreateFilterDialog } from "./components/filters/CreateFilterDialog";
import { CreateVirtualFolderDialog } from "./components/folders/CreateVirtualFolderDialog";
import { SyncProgressPanel } from "./components/layout/SyncProgressPanel";
import { useAccounts } from "./hooks/useApi";
import type { VirtualFolderDto } from "../shared/types";

/** State for the create sender-filter/group dialog. */
export type FilterDialogState = { mode: "group" | "filter"; prefillAddress?: string };

/** null = Dashboard tab, number = account id. */
export type ActiveTab = number | null;

function tabFromUrl(): ActiveTab {
  const value = new URLSearchParams(window.location.search).get("account");
  const id = Number(value);
  return value && Number.isInteger(id) ? id : null;
}

function Workspace() {
  const { data: accounts } = useAccounts();
  const [activeTab, setActiveTab] = useState<ActiveTab>(tabFromUrl);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  // null = "All"; a filter id; or OTHER_FILTER_ID (-1) for "no label".
  const [selectedFilter, setSelectedFilter] = useState<number | null>(null);
  const [hiddenAccountIds, setHiddenAccountIds] = useState<Set<number>>(new Set());
  // The "Run filters (AI)" dialog is triggered from the sidebar but rendered by
  // MailView (which owns the loaded messages + categorize mutation).
  const [runFiltersOpen, setRunFiltersOpen] = useState(false);
  // Sender-based filters: which one is active, and the create dialog.
  const [selectedSenderFilter, setSelectedSenderFilter] = useState<number | null>(null);
  const [filterDialog, setFilterDialog] = useState<FilterDialogState | null>(null);
  // Virtual folders (DB-only): active folder + create/rename dialog.
  const [selectedVirtualFolder, setSelectedVirtualFolder] = useState<number | null>(null);
  const [vfolderDialog, setVfolderDialog] = useState<{
    folder?: VirtualFolderDto;
    prefillAddress?: string;
  } | null>(null);

  const selectSenderFilter = (id: number | null) => {
    setSelectedSenderFilter(id);
    setSelectedFilter(null); // sender-filter view supersedes the AI-label narrowing
    setSelectedVirtualFolder(null);
    // Sender-filters are user-global: run them across every account (the "All"
    // tab, id 0) so results/reading/delete all use the combined prefixed ids —
    // otherwise a single-account tab only searches that one mailbox.
    if (id != null) {
      setActiveTab(0);
      setSelectedFolder(null);
    }
  };

  // Same shape as sender filters: virtual folders are user-global, so the
  // view always runs on the "All" tab with combined-prefixed ids.
  const selectVirtualFolder = (id: number | null) => {
    setSelectedVirtualFolder(id);
    setSelectedSenderFilter(null);
    setSelectedFilter(null);
    if (id != null) {
      setActiveTab(0);
      setSelectedFolder(null);
    }
  };

  const toggleAccount = (id: number) => {
    setHiddenAccountIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Mail sync is manual (the sync button) or server-triggered when the
  // new-mail watcher sees inbox counts change — no periodic full sweeps.
  const [authError] = useState(() =>
    new URLSearchParams(window.location.search).get("auth_error"),
  );
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [devToolsOpen, setDevToolsOpen] = useState(false);

  // Keep ?account= in the URL so the OAuth callback redirect and reloads
  // land on the right tab.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (activeTab === null) url.searchParams.delete("account");
    else url.searchParams.set("account", String(activeTab));
    window.history.replaceState(null, "", url);
  }, [activeTab]);

  // If the URL points at an account that no longer exists, fall back to
  // Dashboard. Id 0 is the virtual "All" tab and always valid.
  useEffect(() => {
    if (activeTab !== null && activeTab !== 0 && accounts && !accounts.some((a) => a.id === activeTab)) {
      setActiveTab(null);
    }
  }, [accounts, activeTab]);

  const selectTab = (tab: ActiveTab) => {
    setActiveTab(tab);
    setSelectedFolder(null);
    setSelectedFilter(null);
    setSelectedSenderFilter(null);
    setSelectedVirtualFolder(null);
    setHiddenAccountIds(new Set());
    setRunFiltersOpen(false);
  };

  // Folder switches remount MailView; don't let a stale "run filters" dialog ride along.
  const selectFolder = (folderId: string | null) => {
    setSelectedFolder(folderId);
    setSelectedSenderFilter(null);
    setSelectedVirtualFolder(null);
    setRunFiltersOpen(false);
  };

  return (
    <div className="flex h-full flex-col">
      <Header
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenDevTools={() => setDevToolsOpen(true)}
      />
      {settingsOpen && <SettingsDialog onClose={() => setSettingsOpen(false)} />}
      {devToolsOpen && <DevToolsDialog onClose={() => setDevToolsOpen(false)} />}
      <div className="flex min-h-0 flex-1">
        <NavPane
          activeTab={activeTab}
          selectedFolder={selectedFolder}
          onSelectFolder={selectFolder}
          selectedFilter={selectedFilter}
          onSelectFilter={setSelectedFilter}
          onRunFilters={() => setRunFiltersOpen(true)}
          selectedSenderFilter={selectedSenderFilter}
          onSelectSenderFilter={selectSenderFilter}
          onCreateGroup={() => setFilterDialog({ mode: "group" })}
          selectedVirtualFolder={selectedVirtualFolder}
          onSelectVirtualFolder={selectVirtualFolder}
          onCreateVirtualFolder={() => setVfolderDialog({})}
          hiddenAccountIds={hiddenAccountIds}
          onToggleAccount={toggleAccount}
        />
        <main className="flex min-w-0 flex-1 flex-col">
          <TabBar accounts={accounts ?? []} activeTab={activeTab} onSelect={selectTab} />
          {authError && activeTab === null && (
            <div className="mx-4 mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-800">
              Sign-in failed: {authError}
            </div>
          )}
          <div className="min-h-0 flex-1">
            {activeTab === null ? (
              <DashboardTab accounts={accounts ?? []} onOpen={selectTab} />
            ) : (
              <MailView
                key={`${activeTab}:${selectedFolder ?? "inbox"}`}
                accountId={activeTab}
                folderId={selectedFolder}
                selectedFilter={selectedFilter}
                hiddenAccountIds={hiddenAccountIds}
                runFiltersOpen={runFiltersOpen}
                onCloseRunFilters={() => setRunFiltersOpen(false)}
                selectedSenderFilter={selectedSenderFilter}
                onClearSenderFilter={() => setSelectedSenderFilter(null)}
                selectedVirtualFolder={selectedVirtualFolder}
                onClearVirtualFolder={() => setSelectedVirtualFolder(null)}
                onEditVirtualFolder={(folder) => setVfolderDialog({ folder })}
                onCreateFilterFromSender={(address) =>
                  setFilterDialog({ mode: "filter", prefillAddress: address })
                }
                onCreateFolderFromSender={(address) =>
                  setVfolderDialog({ prefillAddress: address })
                }
              />
            )}
          </div>
        </main>
      </div>
      <SyncProgressPanel />
      {filterDialog && (
        <CreateFilterDialog
          mode={filterDialog.mode}
          prefillAddress={filterDialog.prefillAddress}
          onClose={() => setFilterDialog(null)}
          onCreated={(filterId) => {
            setFilterDialog(null);
            if (filterId != null) selectSenderFilter(filterId);
          }}
        />
      )}
      {vfolderDialog && (
        <CreateVirtualFolderDialog
          folder={vfolderDialog.folder}
          prefillAddress={vfolderDialog.prefillAddress}
          onClose={() => setVfolderDialog(null)}
          onSaved={(folderId) => {
            setVfolderDialog(null);
            if (folderId != null) selectVirtualFolder(folderId);
          }}
        />
      )}
    </div>
  );
}

export function App() {
  return (
    <>
      <Show when="signed-out">
        <LandingPage />
      </Show>
      <Show when="signed-in">
        <Workspace />
      </Show>
    </>
  );
}
